import { useState, useEffect } from 'react';
import { Play, Pause, Volume2, VolumeX, X, Music, Disc3, ThumbsUp, ThumbsDown, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { usePlayer } from '../context/PlayerContext';
import { useAuth, BACKEND_URL } from '../context/AuthContext';

const SOCIAL_ICONS = {
  instagram: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
  twitter: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
  facebook: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
  youtube: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
  tiktok: <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>,
};

const GlobalPlayer = () => {
  const player = usePlayer();
  const { user } = useAuth();
  const { playingTrack, isPlaying, tuning, isLive, liveNowPlaying, audioRef, togglePlay, stopPlayer } = player || {};

  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [liveSeconds, setLiveSeconds] = useState(0);
  const [socialLinks, setSocialLinks] = useState({});
  const [weather, setWeather] = useState(null);
  const [infoSlide, setInfoSlide] = useState(0);
  const [clockTime, setClockTime] = useState("");

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/public/social-links`)
      .then(r => r.ok ? r.json() : {})
      .then(setSocialLinks)
      .catch(() => {});
  }, []);

  // Weather fetch (once on mount)
  useEffect(() => {
    fetch("https://wttr.in/?format=j1")
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        const cur = d.current_condition?.[0];
        const area = d.nearest_area?.[0];
        if (cur) {
          const code = parseInt(cur.weatherCode, 10);
          let icon = "☀️";
          if ([176, 263, 266, 293, 296, 299, 302, 305, 308, 311, 314, 356, 359].includes(code)) icon = "🌧️";
          else if ([179, 227, 230, 323, 326, 329, 332, 335, 338, 368, 371, 374, 377, 392, 395].includes(code)) icon = "🌨️";
          else if ([200, 386, 389].includes(code)) icon = "⛈️";
          else if ([116, 119, 122].includes(code)) icon = "☁️";
          else if (code === 248 || code === 260) icon = "🌫️";
          else if (code === 113) {
            const hour = new Date().getHours();
            icon = (hour >= 6 && hour < 18) ? "☀️" : "🌙";
          }
          setWeather({
            temp: cur.temp_F,
            icon,
            city: area?.areaName?.[0]?.value || "",
          });
        }
      })
      .catch(() => {});
  }, []);

  // Clock
  useEffect(() => {
    const update = () => setClockTime(new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }));
    update();
    const id = setInterval(update, 30000);
    return () => clearInterval(id);
  }, []);

  // Rotate info slides every 5s
  useEffect(() => {
    const id = setInterval(() => setInfoSlide(s => (s + 1) % 3), 5000);
    return () => clearInterval(id);
  }, []);

  const [myVote, setMyVote] = useState(null);
  const [voteCounts, setVoteCounts] = useState({ up: 0, down: 0 });

  useEffect(() => {
    if (!audioRef?.current) return;
    audioRef.current.volume = muted ? 0 : volume;
  }, [volume, muted, audioRef]);

  // Track audio time via events
  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;
    const onTime = () => setCurrentTime(audio.currentTime || 0);
    const onMeta = () => {
      const d = audio.duration;
      setDuration(d && isFinite(d) ? d : 0);
    };
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('durationchange', onMeta);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('durationchange', onMeta);
    };
  }, [audioRef]);

  // Live stream elapsed timer
  useEffect(() => {
    if (!isLive || !isPlaying) { setLiveSeconds(0); return; }
    const start = Date.now();
    const id = setInterval(() => setLiveSeconds(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [isLive, isPlaying]);

  // Fetch vote state when track changes
  useEffect(() => {
    if (!playingTrack) return;
    const tid = String(playingTrack.id);
    // Get counts (public)
    fetch(`${BACKEND_URL}/api/discover/vote-counts`)
      .then(r => r.ok ? r.json() : {})
      .then(data => setVoteCounts(data[tid] || { up: 0, down: 0 }))
      .catch(() => {});
    // Get my vote
    if (user) {
      const token = localStorage.getItem('token');
      fetch(`${BACKEND_URL}/api/discover/votes`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : {})
        .then(data => setMyVote(data[tid] || null))
        .catch(() => {});
    }
  }, [playingTrack?.id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVote = async (vote) => {
    if (!user || !playingTrack) return;
    const token = localStorage.getItem('token');
    const tid = String(playingTrack.id);
    try {
      const res = await fetch(`${BACKEND_URL}/api/discover/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ track_id: tid, vote }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'removed') {
          setVoteCounts(prev => ({ ...prev, [vote]: Math.max(0, (prev[vote] || 0) - 1) }));
          setMyVote(null);
        } else {
          const prevVote = myVote;
          setVoteCounts(prev => {
            const c = { ...prev };
            c[vote] = (c[vote] || 0) + 1;
            if (prevVote && prevVote !== vote) c[prevVote] = Math.max(0, (c[prevVote] || 0) - 1);
            return c;
          });
          setMyVote(vote);
        }
      }
    } catch (e) { /* silent */ }
  };

  if (!playingTrack) return null;

  const fmtTime = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };
  const fmtLive = (s) => {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}:${String(sec).padStart(2, '0')}`;
  };

  const displayTitle = isLive && liveNowPlaying ? liveNowPlaying.title : playingTrack.title;
  const displayArtist = isLive && liveNowPlaying ? liveNowPlaying.artist : (playingTrack.artists?.join(', ') || '');
  const displayArt = isLive && liveNowPlaying?.art ? liveNowPlaying.art : playingTrack.thumb;
  const progressPct = duration > 0 && !isLive ? (currentTime / duration) * 100 : 0;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] bg-[#0a0a12]/95 backdrop-blur-xl border-t border-white/10" data-testid="global-player">
      {/* Progress bar */}
      {!isLive && duration > 0 && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-white/5">
          <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${progressPct}%` }} />
        </div>
      )}

      <div className="max-w-screen-xl mx-auto flex items-center gap-3 px-4 py-2.5">
        {/* Album art */}
        <div className="w-11 h-11 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
          {displayArt ? (
            <img src={displayArt} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Music className="w-5 h-5 text-white/20" />
            </div>
          )}
        </div>

        {/* Track info */}
        <div className="min-w-0 flex-1 max-w-[200px]">
          <div className="flex items-center gap-2">
            {tuning && <Disc3 className="w-3.5 h-3.5 text-purple-400 animate-spin flex-shrink-0" />}
            <p className="text-white text-sm font-medium truncate">{tuning ? 'Tuning...' : displayTitle}</p>
          </div>
          <p className="text-white/40 text-xs truncate">{displayArtist}</p>
        </div>

        {/* Thumbs Up/Down */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={() => handleVote('up')}
            className={`flex items-center gap-1 p-1.5 rounded-full transition text-xs ${myVote === 'up' ? 'text-green-400 bg-green-500/10' : 'text-white/25 hover:text-green-400 hover:bg-white/5'}`}
            data-testid="global-vote-up"
          >
            <ThumbsUp className="w-3.5 h-3.5" />
            {voteCounts.up > 0 && <span>{voteCounts.up}</span>}
          </button>
          <button
            onClick={() => handleVote('down')}
            className={`flex items-center gap-1 p-1.5 rounded-full transition text-xs ${myVote === 'down' ? 'text-red-400 bg-red-500/10' : 'text-white/25 hover:text-red-400 hover:bg-white/5'}`}
            data-testid="global-vote-down"
          >
            <ThumbsDown className="w-3.5 h-3.5" />
            {voteCounts.down > 0 && <span>{voteCounts.down}</span>}
          </button>
        </div>

        {/* Time display */}
        {isLive && isPlaying && liveSeconds > 0 ? (
          <span className="text-green-400 text-xs font-mono tabular-nums flex-shrink-0" data-testid="global-player-live-time">
            {fmtLive(liveSeconds)}
          </span>
        ) : !isLive && duration > 0 ? (
          <span className="text-white/40 text-xs font-mono tabular-nums flex-shrink-0" data-testid="global-player-time">
            {fmtTime(currentTime)} / {fmtTime(duration)}
          </span>
        ) : null}

        {/* Badges */}
        {isLive && (
          <div className="hidden sm:flex items-center gap-1.5 bg-green-500/20 border border-green-500/30 px-2 py-0.5 rounded-full flex-shrink-0">
            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
            <span className="text-green-400 text-[10px] font-bold">LIVE</span>
          </div>
        )}
        {!isLive && !tuning && (
          <div className="hidden sm:flex items-center gap-1 bg-red-500/20 border border-red-500/30 px-2 py-0.5 rounded-full flex-shrink-0">
            <span className="text-red-400 text-[10px] font-bold">PREVIEW</span>
          </div>
        )}

        {/* Play/Pause */}
        <button
          onClick={togglePlay}
          className="w-9 h-9 rounded-full bg-white flex items-center justify-center flex-shrink-0 hover:scale-105 transition-transform"
          data-testid="global-player-toggle"
        >
          {isPlaying ? <Pause className="w-4 h-4 text-black" /> : <Play className="w-4 h-4 text-black ml-0.5" />}
        </button>

        {/* Volume */}
        <div className="hidden sm:flex items-center gap-2">
          <button onClick={() => setMuted(!muted)} className="text-white/40 hover:text-white/70 transition">
            {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <input
            type="range" min="0" max="1" step="0.05"
            value={muted ? 0 : volume}
            onChange={(e) => { setVolume(parseFloat(e.target.value)); setMuted(false); }}
            className="w-20 accent-green-500 h-1"
          />
        </div>

        {/* Divider */}
        <div className="hidden sm:block w-px h-5 bg-white/[0.06] flex-shrink-0" />

        {/* Rotating Info Ticker: Weather → Time → Listeners */}
        <div className="flex items-center gap-1.5 flex-shrink-0 min-w-[70px] sm:min-w-[120px]" data-testid="mini-player-info-ticker">
          <div className="overflow-hidden h-4 relative w-full">
            {/* Weather */}
            <div className={`absolute inset-0 flex items-center gap-1.5 transition-all duration-500 ${infoSlide === 0 ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3"}`}>
              {weather ? (
                <>
                  <span className="text-[11px] leading-none">{weather.icon}</span>
                  <span className="text-[11px] text-white/30 font-mono">{weather.temp}°F</span>
                  <span className="hidden sm:inline text-[10px] text-white/25 truncate max-w-[70px]">{weather.city}</span>
                </>
              ) : (
                <span className="text-[10px] text-white/20">--</span>
              )}
            </div>
            {/* Time */}
            <div className={`absolute inset-0 flex items-center gap-1.5 transition-all duration-500 ${infoSlide === 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              <span className="text-[11px] leading-none">🕐</span>
              <span className="text-[11px] text-white/30 font-mono">{clockTime}</span>
            </div>
            {/* Listeners */}
            <div className={`absolute inset-0 flex items-center gap-1.5 transition-all duration-500 ${infoSlide === 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
              <span className="text-[11px] leading-none">🎧</span>
              <span className="text-[11px] text-white/30 font-mono">Live 24/7</span>
            </div>
          </div>
        </div>

        {/* Social Icons + DMCA */}
        <div className="hidden md:flex items-center gap-3 flex-1 justify-end" data-testid="mini-player-social">
          {Object.entries(socialLinks).map(([key, url]) => (
            url && SOCIAL_ICONS[key] ? (
              <a key={key} href={url} target="_blank" rel="noopener noreferrer"
                className="text-white/30 hover:text-white transition" title={key} data-testid={`mini-social-${key}`}>
                {SOCIAL_ICONS[key]}
              </a>
            ) : null
          ))}
          <Link to="/dmca" className="flex items-center gap-1.5 text-white/30 hover:text-white/60 transition text-[10px]" data-testid="mini-player-dmca">
            <Shield className="w-3.5 h-3.5" />
            <span>DMCA</span>
          </Link>
        </div>

        {/* Close */}
        <button
          onClick={stopPlayer}
          className="text-white/30 hover:text-white/60 transition p-1.5"
          title="Close player"
          data-testid="global-player-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default GlobalPlayer;
